const sensitiveKey = /token|secret|password|credential|authorization|cookie|signature|api.?key|email|invite|share.?code|return.?to|filename|demo.?url|provider.*url|^(?:query|params|parameters|body|payload|requestBody|responseBody)$/i;

export function telemetryPath(path: string) {
  return path.split(/[?#]/, 1)[0]
    .replace(/\/(profile|match|replays|coaching-clips|match-highlights)\/[^/]+/g, "/$1/:id")
    .replace(/\/(?:\d{6,}|[0-9a-f]{8}-[0-9a-f-]{27,}|CSGO-[A-Za-z0-9-]+)(?=\/|$)/gi, "/:id");
}
export function redactTelemetryText(value: string) {
  return value.replace(/https?:\/\/[^\s"<>]+/g, (raw) => {
    try { const url = new URL(raw); return `${url.origin}${telemetryPath(url.pathname)}`; } catch { return "[URL]"; }
  }).replace(/(Bearer|Basic)\s+[A-Za-z0-9+/=._-]+/gi, "$1 [redacted]")
    .replace(/((?:token|secret|password|key|authorization|cookie|signature)\s*[=:]\s*)[^\s&,;]+/gi, "$1[redacted]")
    .replace(/\bCSGO(?:-[A-Za-z0-9]{5}){5}\b/g, "[share-code]")
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, "[email]")
    .replace(/\b\d{17}\b/g, "[steam-id]").slice(0, 4000);
}
export function redactTelemetryValue(value: unknown, depth = 0, seen = new WeakSet<object>()): unknown {
  if (depth > 8) return "[depth limit]";
  if (typeof value === "string") return redactTelemetryText(value);
  if (typeof value === "symbol" || typeof value === "function") return String(value).slice(0, 200);
  if (typeof value === "bigint") return value.toString();
  if (!value || typeof value !== "object") return value;
  if (seen.has(value)) return "[Circular]";
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  if (value instanceof URL) return redactTelemetryText(value.toString());
  seen.add(value);
  try {
    if (Array.isArray(value)) return value.slice(0, 100).map((entry) => redactTelemetryValue(entry, depth + 1, seen));
    if (value instanceof Map) return redactTelemetryValue(Array.from(value.entries()), depth + 1, seen);
    if (value instanceof Set) return redactTelemetryValue(Array.from(value), depth + 1, seen);
    const fields = value instanceof Error ? { ...value, name: value.name, message: value.message, stack: value.stack, cause: value.cause } : value;
    return Object.fromEntries(Object.entries(fields).slice(0, 100).map(([key, entry]) => [key, sensitiveKey.test(key) ? "[redacted]" : redactTelemetryValue(entry, depth + 1, seen)]));
  } finally { seen.delete(value); }
}

const allowedProperties = new Set([
  "app_id", "app_release", "product_surface", "route_group", "route_path", "current_pathname", "current_hostname", "current_origin", "auth_state", "auth_provider", "navigation_surface",
  "plan_key", "billing_interval", "interval", "subscription_status", "is_paid", "mrr", "amount_total", "currency", "credits", "bytes", "reason", "status", "flow", "event_type", "source", "source_type", "method", "error_type",
  "map_name", "round_number", "duration_seconds", "elapsed_ms", "imported_files", "file_count", "video_count", "player_count", "result", "feature", "step", "tab", "mode",
  "has_completed_parse", "has_opened_replay", "has_saved_cloud_replay", "has_team_workspace",
  "$current_url", "$referrer", "$pathname", "$host", "$browser", "$os", "$device_type", "$screen_height", "$screen_width", "$viewport_height", "$viewport_width", "$lib", "$lib_version",
]);
export function analyticsProperties(properties: Record<string, unknown>) {
  const result = Object.fromEntries(Object.entries(properties).filter(([key, value]) => allowedProperties.has(key) && (value == null || ["string", "number", "boolean"].includes(typeof value))).map(([key, value]) => [key, key === "reason" && typeof value === "string" && !/^[a-z][a-z0-9_]{0,80}$/.test(value) ? "unclassified" : typeof value === "string" && /path|route/.test(key) ? telemetryPath(value) : redactTelemetryValue(value)]));
  // Correlate a consented login attempt without admitting URLs, credentials, or
  // arbitrary identifiers into the analytics property allowlist.
  const attemptId = properties.auth_attempt_id;
  if (typeof attemptId === "string" && /^[a-zA-Z0-9_-]{1,100}$/.test(attemptId)) result.auth_attempt_id = attemptId;
  for (const key of ["entry_surface", "current_plan_key", "storage_backend"]) {
    const value = properties[key];
    if (typeof value === "string" && /^[a-z][a-z0-9_]{0,80}$/.test(value)) result[key] = value;
  }
  if (typeof properties.is_cloud_saved === "boolean") result.is_cloud_saved = properties.is_cloud_saved;
  return result;
}


// SDK envelope identifiers are required for consented events to remain valid.
// These are distinct from application-auth/provider credentials and never enter logs.
export function browserAnalyticsProperties(properties: Record<string, unknown>) {
  const result: Record<string, unknown> = analyticsProperties(properties);
  for (const key of ["token", "distinct_id", "$device_id", "$user_id", "$anon_distinct_id", "$session_id", "$window_id", "$insert_id"]) {
    const value = properties[key];
    if (typeof value === "string" && /^[a-zA-Z0-9._:-]{1,200}$/.test(value)) result[key] = value;
  }
  for (const key of ["$set", "$set_once"]) {
    const value = properties[key];
    if (value && typeof value === "object" && !Array.isArray(value)) result[key] = analyticsProperties(value as Record<string, unknown>);
  }
  return result;
}
